import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check } from "lucide-react";

const KIND_ACCENT = {
  quotation_created: "bg-purple-50 text-purple-800",
  quotation_sent: "bg-purple-50 text-purple-800",
  invoice_sent: "bg-blue-50 text-blue-700",
  invoice_auto_drafted: "bg-amber-50 text-amber-800",
  payment_received: "bg-emerald-50 text-emerald-700",
  credit_note_issued: "bg-anvi-100 text-anvi-700",
};

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await api.notifications();
      setItems(r.data.notifications);
      setUnread(r.data.unread_count);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);  // poll every 30s
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const markAllRead = async () => {
    try {
      await api.notificationsMarkRead();
      load();
    } catch { /* silent */ }
  };

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((v) => !v)} data-testid="notification-bell"
              className="relative p-2 hover:bg-anvi-100 rounded-md transition-colors">
        <Bell size={18} strokeWidth={1.5} className="text-anvi-700" />
        {unread > 0 && (
          <span data-testid="notification-badge"
                className="absolute top-0 right-0 w-4 h-4 rounded-full bg-anvi-terracotta text-white text-[10px] font-medium flex items-center justify-center tabular-nums">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white border border-anvi-200 rounded-md shadow-lg z-50" data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-anvi-200">
            <div>
              <div className="text-xs tracking-[0.15em] uppercase text-anvi-500">Notifications</div>
              <div className="font-heading text-lg text-anvi-900">{unread} unread</div>
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} data-testid="mark-all-read"
                      className="text-xs text-anvi-terracotta hover:underline flex items-center gap-1">
                <Check size={11} strokeWidth={2} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-anvi-500">No notifications yet</div>
            ) : items.map((n) => {
              const Wrapper = n.link ? Link : "div";
              const wrapperProps = n.link ? { to: n.link, onClick: () => setOpen(false) } : {};
              return (
                <Wrapper key={n.id} {...wrapperProps} data-testid={`notif-${n.id}`}
                         className={`block px-4 py-3 border-b border-anvi-100 last:border-b-0 hover:bg-anvi-50 transition-colors ${n.is_read ? "opacity-70" : ""}`}>
                  <div className="flex items-start gap-3">
                    {!n.is_read && <div className="w-1.5 h-1.5 rounded-full bg-anvi-terracotta mt-1.5 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${KIND_ACCENT[n.kind] || "bg-anvi-100 text-anvi-700"}`}>
                          {n.kind.replace(/_/g, " ")}
                        </span>
                        <span className="text-xs text-anvi-400 tabular-nums">{new Date(n.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                      <div className="text-sm text-anvi-900">{n.title}</div>
                      {n.body && <div className="text-xs text-anvi-500 mt-0.5 truncate">{n.body}</div>}
                    </div>
                  </div>
                </Wrapper>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
